import { CustomerMeetingUserState } from '@libs/focus-frontend-types/src/@teamleader/api/customerMeetingUsers';

import privacyAndTermsUrls from '../../static/privacyAndTermsUrls.json';
import { Permission, User, getAuthenticatedUser } from '../AuthenticationService';
import { AccountFeatureFlag, FeatureFlag, getAccountFeatureFlag, get as getFeatureFlag } from '../FeatureFlagService';
import PreferencesService from '../PreferencesService';

import { MobilePlatform } from './@types';

export const root = (parameterString = '') => `/${parameterString}`;
export const appRoot = () => environment.APP_URL;

export const backendRoot = () => environment.BACKEND_URL;

export const statusPage = () => 'https://status.teamleader.eu';

export const manageSubscription = () => {
  return '/web/settings/subscription';
};

export const subscriptionDetail = (subscriptionId: string) => `/subscription_detail.php?id=${subscriptionId}`;

export const creditNoteDetail = (creditNoteId: string) => `/creditnote_detail.php?id=${creditNoteId}`;

export const upgradeDemoAccount = () => {
  return '/web/settings/subscription/start';
};

export const startPackageTrial = (packageId = '') => {
  packageId = packageId.startsWith('/') ? packageId : '/' + packageId;
  return `/web/settings/subscription/trial${packageId.toLowerCase()}`;
};

export const customerMeetingSsoStandalone = (language: string, next?: string) => {
  const baseUrl = `${environment.VECTERA_URL}/social-accounts/teamleader-login/?lang=${language}`;

  if (next) {
    return `${baseUrl}&next=${next}`;
  }

  return baseUrl;
};
export const customerMeetingContactForms = (language: string) =>
  customerMeetingSsoStandalone(language, '/contact-forms/add');
export const customerMeetingContactFormsForActivatedUsers = (language: string, skipWelcome = false) =>
  customerMeetingSsoStandalone(language, '/contact-forms' + (skipWelcome ? '/?skipWelcome' : ''));
export const customerMeetingContactFormsInAppPage = () => `/contact-forms`;
export const customerMeetingPromotionalPage = (next?: string) => {
  const baseUrl = `/try-customer-meeting`;

  if (next) {
    return `${baseUrl}?next=${next}`;
  }

  return baseUrl;
};
export const customerMeetingLearnMore = (language: string) => {
  switch (language) {
    case 'nl-BE':
      return 'https://www.teamleader.be/focus/nl-be/functie/customer-meeting-in-app';
    case 'fr-BE':
      return 'https://www.teamleader.be/focus/fr-be/fonctionnalite/customer-meeting-in-app';
    case 'de-DE':
      return 'https://www.teamleader.de/focus/funktion/customer-meeting-in-app';
    case 'fr-FR':
      return 'https://www.teamleader.fr/focus/fonctionnalite/customer-meeting-in-app';
    case 'it-IT':
      return 'https://www.teamleadercrm.it/focus/funzione/customer-meeting-in-app';
    case 'nl-NL':
      return 'https://www.teamleader.nl/focus/functie/customer-meeting-in-app';
    case 'es-ES':
      return 'https://www.teamleader.es/focus/funcionalidad/customer-meeting-in-app';
    default:
      return 'https://www.teamleader.eu/focus/feature/customer-meeting-in-app';
  }
};

export const customerMeeting = (state: CustomerMeetingUserState, language: string) =>
  state === CustomerMeetingUserState.Active ? customerMeetingSsoStandalone(language) : customerMeetingPromotionalPage();
export const upgradeCustomerMeetingTrial = () => '/web/settings/subscription/customer-meeting/upgrade';
export const addCustomerMeetingUsers = () => '/web/settings/subscription/customer-meeting/add-seats';

export const marketplace = () => environment.MARKETPLACE_URL;
export const marketplaceBuild = () => `${marketplace()}/build`;
export const marketplaceIntegration = (reference: string) => `${marketplace()}/integration/${reference}`;
export const marketplaceLogin = () => `${marketplace()}/login`;
export const marketplaceManage = () => `${marketplace()}/manage`;
export const marketplaceCategory = (category: string) => `${marketplace()}/eu/en/category/${category}`;

export const dashboard = (parameterString = '') => {
  const isDashingDashboardFeatureFlagEnabled = getAccountFeatureFlag(AccountFeatureFlag.DashingDashboard, false);

  if (isDashingDashboardFeatureFlagEnabled) {
    return `/dashboard${parameterString}`;
  }

  return `/dashboard.php${parameterString}`;
};
export const calendar = (parameterString = '') => `/planning.php${parameterString}`;

export const invoiceOverview = (parameterString = '') => `/invoices.php${parameterString}`;
export const invoiceDetail = (id: string) => `/invoice_detail.php?id=${id}`;
export const invoiceForecast = () => '/web/invoicing/invoices/forecast';

export const dealOverview = (parameterString = '') => `/sales.php${parameterString}`;
export const dealPipeline = (parameterString = '') => `/web/deals/pipeline${parameterString}`;
export const dealDetail = (id: string, parameterString = '') => `/web/deals/${id}${parameterString}`;
export const dealForecast = (parameterString = '') => `/web/deals/forecast/${parameterString}`;

export const quotationOverview = (parameterString = '') => `/quotations/overview${parameterString}`;
export const quotationDetail = (parameterString = '') => `/quotations/${parameterString}`;

export const contactOverview = (parameterString = '') => `/contacts.php${parameterString}`;
export const contactDetail = (id: string) => `/contacts/${id}`;
export const contactDetailCustomise = (id: string) => `/contacts/${id}/customise`;

export const companyOverview = (parameterString = '') => `/companies.php${parameterString}`;
export const companyDetail = (id: string) => `/companies/${id}`;
export const companyDetailCustomise = (id: string) => `/companies/${id}/customise`;

export const taskOverview = () => `/tasks.php`;
export const taskDetail = (taskId: string) => `/task_detail.php?id=${taskId}`;

export const ticketOverview = (parameterString = '') => `/tickets.php${parameterString}`;
export const ticketDetail = (ticketId: string) => `/ticket_detail.php?id=${ticketId}`;

export const meetingDetail = (meetingId: string) => `/meeting_detail.php?id=${meetingId}`;

export const productOverview = (parameterString = '') => `/products.php${parameterString}`;
export const productDetail = (productId: string) => `/product_detail.php?id=${productId}`;

export const documentOverview = (parameterString = '') => `/documents.php${parameterString}`;

export const insightOverview = (parameterString = '') => `/stats.php${parameterString}`;

export const timesheetOverview = (parameterString = '') => `/timesheets.php${parameterString}`;
export const timesheetProjectDetail = (projectId: string) => `/timesheets_detail.php?project_id=${projectId}`;

export const workorderOverview = (parameterString = '') => `web/workorders${parameterString}`;
export const workorderDetail = (workorderId: string) => `web/workorders/${workorderId}`;

export const mailingOverview = () => '/mailing.php';

export const fileHistory = (fileId: string) => `/file_history.php?id=${fileId}`;

export const settings = (parameterString = '') => `/settings.php${parameterString}`;
export const documentLayouts = (departmentId?: string) => {
  const path = 'settings/document-layouts';

  if (departmentId) {
    return `${path}/${departmentId}`;
  }

  return path;
};
export const hrUserSettings = (tab?: 'active' | 'invited' | 'deactivated') => {
  return tab && tab !== 'active' ? `/settings/users/${tab}` : '/settings/users';
};
export const hrUserSettingsEmployee = (employeeId: string) => {
  return `/employee_detail.php?id=${employeeId}`;
};

export const exportsList = () => 'exports/list';

export const cloudPlatformSettings = () => '/settings.php#cloudplatforms';
export const salesSettings = (parameterString = '') => `/settings.php${parameterString}#nav_sales`;
export const invoiceSettings = () => '/settings.php#nav_invoices';

export const departmentLogo = (id: string) =>
  `${backendRoot()}/ajax/getDepartmentLogo.php?noFallback=true&id=${encodeURIComponent(id)}`;

export const projectOverview = (parameterString = '') => {
  if (getAuthenticatedUser(User.Permissions).includes(Permission.NextgenProjects)) {
    return '/projects';
  }

  if (!getFeatureFlag(FeatureFlag.ProjectsTimeline)) {
    return `/projects.php${parameterString}`;
  }
  return PreferencesService.get(['webApp', 'project_overview']) === 'timeline'
    ? `/web/projects/timeline${parameterString}`
    : `/projects.php${parameterString}`;
};

export const projectDetail = (projectId: string, phaseId: string | null = null) => {
  if (getAuthenticatedUser(User.Permissions).includes(Permission.NextgenProjects)) {
    return `/projects/${projectId}`;
  }

  if (!getFeatureFlag(FeatureFlag.WorkBreakdownFrontAndCentre)) {
    return `/project_detail.php?id=${projectId}`;
  }

  if (phaseId) {
    return `/web/projects/${projectId}/work-breakdown?sidePanelEntityId=${phaseId}&sidePanelType=taskGroupView&expandedTaskGroupId=${phaseId}`;
  }

  return `/web/projects/${projectId}/work-breakdown`;
};

export const legacyProjectDetail = (projectId: string) => `/web/projects/${projectId}/work-breakdown`;

export const convertQuotationToProject = (quotationId: string) => `/quotations/${quotationId}/convert-to-project`;
export const convertQuotationFromDealToProject = (quotationId: string, dealId: string) =>
  `/web/deals/${dealId}/convert-quotation-to-project?quotationId=${quotationId}`;

export const downloadExport = (uuid: string) => `/app/exports/${uuid}/download`;

export const changeLicense = () => `/web/settings/subscription/change`;
export const changePaymentMethod = () => '/web/settings/subscription/payment/edit';

export const profile = () => '/myaccount.php';
export const referrals = () => '/referrals.php';

export const legacyProjectsOverview = () => `/projects.php`;
export const legacyProjectsTimeline = () => `/web/projects/timeline`;
export const projectsOverview = (parameterString = '') => `/projects${parameterString}`;
export const projectsTimeline = () => `/projects/timeline`;
export const projectsWorkBreakdown = (id: string, parameterString = '') =>
  `/projects/${id}/work-breakdown${parameterString}`;
export const projectsTimeTrackings = (id: string) => `/projects/${id}/time-trackings`;
export const projectsInfo = (id: string) => `/projects/${id}/info`;
export const projectsInvoices = (id: string, createNew = false) =>
  `/projects/${id}/invoices${createNew ? '?create' : ''}`;
export const projectNotFound = () => '/projects?item_not_found';
export const projectsMigration = () => '/projects/migrate';
export const projectsKanban = (id: string, parameterString = '') => `/projects/${id}/kanban${parameterString}`;

const getPrivacyAndTermsUrl = (type: 'dpa' | 'termsOfService' | 'privacy' | 'cookiePolicy') =>
  (privacyAndTermsUrls[window.TLGLOBALS.LANGUAGE as keyof typeof privacyAndTermsUrls] || privacyAndTermsUrls['en'])[
    type
  ];
export const dpa = () => getPrivacyAndTermsUrl('dpa');
export const termsOfService = () => getPrivacyAndTermsUrl('termsOfService');
export const privacyPolicy = () => getPrivacyAndTermsUrl('privacy');
export const cookiePolicy = () => getPrivacyAndTermsUrl('cookiePolicy');
export const eula = () => 'https://www.teamleader.eu/eula';

export const knowledgeBase = () => 'https://support.focus.teamleader.eu';
const knowledgeBaseUrl = (path: string) =>
  `https://support.focus.teamleader.eu/support/solutions${path.startsWith('/') ? path : `/${path}`}`;
const knowledgeBaseFolder = (folderId: string) => knowledgeBaseUrl(`/folders/${folderId}`);
const knowledgeBaseArticle = (articleId: string, utmCampaign: string = 'app+knowledgebase+views') =>
  knowledgeBaseUrl(`/articles/${articleId}?utm_campaign=${utmCampaign}`);

const knowledgeBaseArticleForIntroStates = (articleId: string) => knowledgeBaseArticle(articleId, 'app+intro+states');

export const createContactFormsKnowledgeBase = () => knowledgeBaseArticle('7000082753');
export const companiesKnowledgeBase = () => knowledgeBaseFolder('7000008399');
export const contactsKnowledgeBase = () => knowledgeBaseFolder('7000008399');
export const tasksKnowledgeBase = () => knowledgeBaseFolder('7000008401');
export const callsKnowledgeBase = () => knowledgeBaseFolder('7000008401');
export const meetingsKnowledgeBase = () => knowledgeBaseFolder('7000008401');
export const dealsKnowledgeBase = () => knowledgeBaseFolder('7000008400');
export const projectsKnowledgeBase = () => knowledgeBaseFolder('7000008405');
export const nextgenProjectsKnowledgeBase = () => knowledgeBaseFolder('7000047546');
export const invoicesKnowledgeBase = () => knowledgeBaseFolder('7000008403');
export const ticketsKnowledgeBase = () => knowledgeBaseFolder('7000008406');
export const productsKnowledgeBase = () => knowledgeBaseFolder('7000008404');
export const timesheetsKnowledgeBase = () => knowledgeBaseFolder('7000008402');
export const privacyAndTermsLearnMoreKnowledgeBase = () => knowledgeBaseFolder('7000044499');
export const creditNotesKnowledgeBase = () => knowledgeBaseArticle('7000019992');
export const subscriptionsKnowledgeBase = () => knowledgeBaseArticle('7000017939');
export const deliveryNotesKnowledgeBase = () => knowledgeBaseArticle('7000018061');
export const ordersKnowledgeBase = () => knowledgeBaseArticle('7000018061');
export const orderConfirmationsKnowledgeBase = () => knowledgeBaseArticle('7000018061');
export const defineInternalCostsOfEmployeesKnowledgeBase = () => knowledgeBaseArticle('7000050793');
export const howToUsePurchasePriceOnOrdersKnowledgeBase = () => knowledgeBaseArticle('7000050538');
export const whyIsMyInvoiceNotEligableForOnDemandFinancingKnowledgeBase = () => knowledgeBaseArticle('7000060344');
export const howToFinanceInvoicesViaOnDemandFinancingKnowledgeBase = () => knowledgeBaseArticle('7000057344');
export const iAmFinancingAnInvoiceDoILetMyClientKnowKnowledgeBase = () => knowledgeBaseArticle('7000060346');
export const mfaKnowledgeBase = () => knowledgeBaseArticle('7000019220');
export const mfaEnforcementKnowledgeBase = () => knowledgeBaseArticle('7000083853');
export const whyAmILoggedOutKnowledgeBase = () => knowledgeBaseArticle('7000080384');
export const privacyAndTermsUpdate202212KnowledgeBase = () => knowledgeBaseArticle('7000076136');
export const expiryDateOnQuotationsKnowledgeBase = () => knowledgeBaseArticle('7000082302');
export const multipleDealPipelinesKnowledgeBase = () => knowledgeBaseArticle('7000084541');
export const weeklyTimeTrackingKnowledgeBase = () => knowledgeBaseArticle('7000066952');
export const belgianLateFeesComplianceKnowledgeBase = () => knowledgeBaseArticle('7000083850');

export const howToAddCompaniesKnowledgeBase = () => knowledgeBaseArticleForIntroStates('7000018902');
export const howToAddContactsKnowledgeBase = () => knowledgeBaseArticleForIntroStates('7000018198');
export const howToAddProjectsKnowledgeBase = () => knowledgeBaseArticleForIntroStates('7000082452');

const applyQueryParametersFromMap = (url: string, queryParameters: Record<string, string | number | boolean> = {}) => {
  const queryString = Object.entries(queryParameters)
    .map((entry) => entry.map(encodeURIComponent).join('='))
    .join('&');

  if (queryString) {
    return `${url}?${queryString}`;
  }

  return url;
};
export const welcomeToOurTemplateGalleryKnowledgeBase = (
  queryParametersAsMap: Record<string, string | number | boolean>,
) => {
  return applyQueryParametersFromMap(knowledgeBaseArticle('7000044977'), queryParametersAsMap);
};
export const dealOverviewKnowledgeBase = () => knowledgeBaseArticle('7000050442');
export const cloudSignKnowledgeBase = () => knowledgeBaseArticle('7000016998');
export const dealDetailKnowledgeBase = () => knowledgeBaseArticle('7000056938');
export const dealForecastKnowledgeBase = () => knowledgeBaseArticle('7000054946');
export const teamleaderFocusForSalesKnowledgeBase = () => knowledgeBaseArticle('7000039387');
export const projectRightsKnowledgeBase = () => knowledgeBaseArticle('7000018702');
export const projectsOverviewKnowledgeBase = () => knowledgeBaseArticle('7000056257');
export const projectCostsKnowledgeBase = () => knowledgeBaseArticle('7000018231');
export const projectBudgetManagementKnowledgeBase = () => knowledgeBaseArticle('7000051473');
export const projectPlanningKnowledgeBase = () => knowledgeBaseArticle('7000059928');
export const exportReportsKnowledgeBase = () => knowledgeBaseArticle('7000018263');
export const fileManagementKnowledgeBase = () => knowledgeBaseArticle('7000058527');
export const copyProjectKnowledgeBase = () => knowledgeBaseArticle('7000017557');
export const billProjectKnowledgeBase = () => knowledgeBaseArticle('7000054800');
export const createInvoiceKnowledgeBase = () => knowledgeBaseArticle('7000020029');
export const emailInvoiceKnowledgeBase = () => knowledgeBaseArticle('7000019547');
export const setupInvoiceCloudKnowledgeBase = () => knowledgeBaseArticle('7000019490');
export const bookkeepingIntegrationsKnowledgeBase = () => knowledgeBaseArticle('7000017870');
export const invoiceRemindersKnowledgeBase = () => knowledgeBaseArticle('7000018073');
export const invoiceForecastKnowledgeBase = () => knowledgeBaseArticle('7000060866');
export const globalSearchKnowledgeBase = () => knowledgeBaseArticle('7000053700');
export const customFieldsAmountKnowledgeBase = () => knowledgeBaseArticle('7000017376');
export const daysOffTimeOrderIsIncorrectKnowledgeBase = () => knowledgeBaseArticle('7000044169');
export const grantAdminRightsKnowledgeBase = () => knowledgeBaseArticle('7000017045');
export const combineLimitedCRMAndLimitedDealsKnowledgeBase = () => knowledgeBaseArticle('7000017964');
export const documentLayoutMaxFileSizeKnowledgeBase = () => knowledgeBaseArticle('7000017233');
export const reduceUsersOnSubscriptionKnowledgeBase = () => knowledgeBaseArticle('7000039214');
export const teamleaderFocusWalletKnowledgeBase = () => knowledgeBaseArticle('7000018072');
export const vatKvkSiretAutofillKnowledgeBase = () => knowledgeBaseArticle('7000048492');
export const whiteLabelDomainsKnowledgeBase = () => knowledgeBaseArticle('7000021083');
export const whiteLabelDomainsTroubleShootGuideKnowledgeBase = () => knowledgeBaseArticle('7000075959');

export const gettingStartedKnowledgeBase = () => knowledgeBaseUrl('7000006462');
export const videoTutorialsKnowledgeBase = () => knowledgeBaseUrl('7000006389');
export const apiDocumentation = () => 'https://developer.focus.teamleader.eu';

export const wistiaPlayer = (videoId: string, controlsVisibleOnLoad: boolean = false, playButton: boolean = true) =>
  `https://fast.wistia.net/embed/iframe/${videoId}?controlsVisibleOnLoad=${controlsVisibleOnLoad}&playButton=${playButton}`;

export const mobileAppSplash = (platform?: MobilePlatform) => {
  return platform ? `/try-mobile-app#${platform}` : '/try-mobile-app';
};

export const mobileAppStore = (platform: MobilePlatform) => {
  switch (platform) {
    case MobilePlatform.Ios:
      return 'https://apps.apple.com/us/app/teamleader-focus/id6453793095';
    case MobilePlatform.Android:
      return 'https://play.google.com/store/apps/details?id=eu.teamleader.focus&referrer=utm_source%3Dknowledge_base%26utm_medium%3Dwebsite';
    default:
      return null;
  }
};
